import { T } from "./Translate";
import React, { useState, useEffect, useCallback } from "react";
import logo23 from "../imgs/logo23.png";
import logo1 from "../imgs/logo1.png";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Checkbox,
  Divider,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { Search as SearchIcon } from "../icons";
import { QRCodeCanvas } from "qrcode.react";
import { auth, db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import PageHeader from "./PageHeader";
import { useNavigate } from "react-router-dom";
import { getOfferPricing } from "./homeUtils";
import { brandColors, themeMode } from "../theme";
import Web3 from "web3";

import SearchClearAdornment from "../components/SearchClearAdornment";
const logo5 = `${process.env.PUBLIC_URL}/logo5.png`;

const PaymentQRGenerator = () => {
    // Web3/Wallet State
    const [walletAddress, setWalletAddress] = useState("");
    const [web3, setWeb3] = useState(null);
    const [ethTxStatus, setEthTxStatus] = useState("");

    // Conectar wallet Ethereum
    const connectWallet = async () => {
      if (window.ethereum) {
        try {
          await window.ethereum.request({ method: "eth_requestAccounts" });
          const web3Instance = new Web3(window.ethereum);
          setWeb3(web3Instance);
          const accounts = await web3Instance.eth.getAccounts();
          setWalletAddress(accounts[0]);
        } catch (error) {
          setEthTxStatus(<T>Error al conectar la wallet: {error.message}</T>);
        }
      } else {
        setEthTxStatus(<T>No se encontró una wallet compatible (MetaMask, Trust Wallet, etc.)</T>);
      }
    };

    // Cobrar en ETH
    const cobrarEnEth = async () => {
      if (!web3 || !walletAddress) {
        setEthTxStatus(<T>Conecta tu wallet primero.</T>);
        return;
      }
      try {
        setEthTxStatus(<T>Enviando solicitud de pago...</T>);
        const toAddress = "0x000000000000000000000000000000000000dEaD";
        const valueEth = totalAmount || 0;
        const valueWei = web3.utils.toWei(valueEth.toString(), "ether");
        await web3.eth.sendTransaction({ from: walletAddress, to: toAddress, value: valueWei });
        setEthTxStatus(<T>¡Cobro en Ethereum realizado con éxito!</T>);
      } catch (error) {
        setEthTxStatus(<T>Error en el cobro: {error.message}</T>);
      }
    };
  const [offers, setOffers] = useState([]);
  const [offerSearchTerm, setOfferSearchTerm] = useState("");
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [qrData, setQRData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);

  const navigate = useNavigate();

  const calculateTotal = useCallback(() => {
    const total = selectedOffers.reduce((sum, offer) => {
      const pricing = getOfferPricing(offer);
      const price = pricing ? pricing.finalPrice : (parseFloat(offer.price) || 0);
      return sum + price;
    }, 0);
    setTotalAmount(total);
  }, [selectedOffers]);

  useEffect(() => {
    const fetchOffers = async () => {
      if (!auth.currentUser) {
        setError(<T>No se encontró un usuario autenticado</T>);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const offersQuery = query(
          collection(db, "offers"),
          where("uid", "==", auth.currentUser.uid)
        );
        const offersSnap = await getDocs(offersQuery);
        
        setOffers(offersSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })));
      } catch (error) {
        console.error("Error fetching offers:", error);
        setError(<T>Error al cargar las ofertas</T>);
      } finally {
        setLoading(false);
      }
    };

    fetchOffers();
  }, []);

  useEffect(() => {
    calculateTotal();
  }, [calculateTotal]);

  const handleOfferToggle = useCallback((offer) => {
    setSelectedOffers(prev => {
      const currentIndex = prev.findIndex(p => p.id === offer.id);
      if (currentIndex === -1) {
        return [...prev, offer];
      }
      const newSelected = [...prev];
      newSelected.splice(currentIndex, 1);
      return newSelected;
    });
  }, []);

  const generateQR = useCallback(() => {
    if (selectedOffers.length === 0) {
      setError(<T>Selecciona al menos una oferta</T>);
      return;
    }

    const qrContent = {
      type: 'payment',
      merchantId: auth.currentUser.uid,
      storeName: auth.currentUser.displayName,
      offers: selectedOffers.map(offer => ({
        id: offer.id,
        title: offer.product,
        price: offer.price,
        offer: offer.offer,
        description: offer.description
      })),
      totalAmount,
      timestamp: new Date().toISOString()
    };

    setQRData(qrContent);
    setError(null);
  }, [selectedOffers, totalAmount]);

  const generateProfileQR = useCallback(() => {
    const qrContent = {
      type: 'profile',
      merchantId: auth.currentUser.uid,
      storeName: auth.currentUser.displayName,
      timestamp: new Date().toISOString()
    };

    setQRData(qrContent);
    setError(null);
  }, []);

  const renderPrice = useCallback((offer) => {
    const discount = parseFloat(offer.offer) || 0;
    const pricing = getOfferPricing(offer);
    const price = pricing ? pricing.finalPrice : (parseFloat(offer.price) || 0);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
        {pricing && (
          <Typography
            variant="body2"
            sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
          >
            ${pricing.previousPrice.toFixed(2)}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {discount > 0 && (
            <Box
              sx={{
                backgroundColor: '#1e4620',
                color: '#baffc9',
                px: 1.1,
                py: 0.4,
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: 700,
              }}
            >
              -{discount}%
            </Box>
          )}
          <Box
            sx={{
              backgroundColor: brandColors.primary,
              color: '#ffffff',
              px: 1.75,
              py: 0.75,
              borderRadius: '8px',
              fontWeight: 700,
              fontSize: '1.15rem',
              boxShadow: '0 2px 6px rgba(3,5,83,0.35)',
            }}
          >
            ${price.toFixed(2)}
          </Box>
        </Box>
      </Box>
    );
  }, []);

  const filteredOffers = offers.filter((offer) => {
    const term = offerSearchTerm.trim().toLowerCase();
    if (!term) return true;
    return [offer.product, offer.description].some(
      (field) => typeof field === "string" && field.toLowerCase().includes(term)
    );
  });

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 100);
  }

  if (isPrinting) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', bgcolor: '#ffffff' }}>
        {/* Siempre el logo navy sobre fondo blanco: esta vista se imprime en
            papel blanco sin importar el tema activo de la app. */}
        <img src={logo1} alt="Logo" style={{ width: '150px', marginBottom: '20px' }} />
        <Typography variant="h5" gutterBottom sx={{ color: '#15171f' }}>
          {auth.currentUser.displayName}
        </Typography>
        <QRCodeCanvas
          id="payment-qr"
          value={JSON.stringify(qrData)}
          size={256}
          level="H"
          includeMargin
        />
        <style>
          {`
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              #payment-qr {
                margin: 0 auto;
              }
            }
          `}
        </style>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: brandColors.backgroundDefault }}>
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Button variant="contained" color="secondary" onClick={connectWallet} sx={{ mb: 1 }}>
          {walletAddress ? <><T>Wallet conectada:</T> {walletAddress}</> : <T>Conectar Wallet Ethereum</T>}
        </Button>
        {walletAddress && (
          <Button variant="contained" color="primary" onClick={cobrarEnEth} sx={{ ml: 2 }}>
            <T>Cobrar</T> {totalAmount} ETH
          </Button>
        )}
        {ethTxStatus && <Alert severity="info" sx={{ mt: 1 }}>{ethTxStatus}</Alert>}
      </Box>
      <PageHeader title={<T>Generar QR</T>} />

      <Container maxWidth="sm" sx={{ pb: 4 }}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 3,
            backgroundColor: brandColors.backgroundPaper,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <img src={themeMode === 'dark' ? logo5 : brandColors.mode === 'light' ? logo1 : logo23} alt="Logo" style={{ width: '100px' }} />
          </Box>
          <Typography variant="h6" gutterBottom>
            <T>Selecciona las ofertas a cobrar (opcional)</T>
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 2 }}>
              {error}
            </Alert>
          ) : offers.length === 0 ? (
            <Alert severity="info" sx={{ my: 2 }}>
              <T>No tienes ofertas publicadas.</T>
              <Button 
                color="primary"
                onClick={() => navigate('/publish-offer')}
                sx={{ ml: 1 }}
              >
                <T>Crear oferta</T>
              </Button>
            </Alert>
          ) : (
            <>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar producto..."
                value={offerSearchTerm}
                onChange={(e) => setOfferSearchTerm(e.target.value)}
                sx={{ mb: 1.5 }}
                InputProps={{
                  endAdornment: <SearchClearAdornment value={offerSearchTerm} onChange={(e) => setOfferSearchTerm(e.target.value)} />,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TableContainer
                sx={{
                  maxHeight: 420,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell><T>Producto</T></TableCell>
                      <TableCell><T>Descripción</T></TableCell>
                      <TableCell align="right"><T>Precio</T></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOffers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                          <T>No hay productos que coincidan con la búsqueda.</T>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredOffers.map((offer) => (
                        <TableRow
                          key={offer.id}
                          hover
                          selected={selectedOffers.some((p) => p.id === offer.id)}
                          onClick={() => handleOfferToggle(offer)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedOffers.some((p) => p.id === offer.id)}
                              onChange={() => handleOfferToggle(offer)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ '&.Mui-checked': { color: '#fff' } }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {offer.product?.length > 50
                              ? `${offer.product.substring(0, 50)}...`
                              : offer.product}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {offer.description?.length > 100
                              ? `${offer.description.substring(0, 100)}...`
                              : offer.description}
                          </TableCell>
                          <TableCell align="right">{renderPrice(offer)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h5" align="center" gutterBottom>
                <T>Total:</T> ${totalAmount.toFixed(2)}
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Button
                  variant={qrData?.type === 'payment' ? 'contained' : 'outlined'}
                  fullWidth
                  color="primary"
                  onClick={generateQR}
                  disabled={selectedOffers.length === 0}
                >
                  <T>Generar QR de Cobro</T>
                </Button>
                <Button
                  variant={qrData?.type === 'profile' ? 'contained' : 'outlined'}
                  fullWidth
                  color="primary"
                  onClick={generateProfileQR}
                >
                  <T>QR General</T>
                </Button>
              </Box>

              {qrData && (
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      display: 'inline-block',
                      p: 2,
                      borderRadius: 2,
                      bgcolor: '#ffffff',
                    }}
                  >
                    <QRCodeCanvas
                      id="payment-qr"
                      value={JSON.stringify(qrData)}
                      size={256}
                      level="H"
                      includeMargin
                    />
                  </Box>
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={handlePrint}
                      sx={{ mt: 2 }}
                    >
                      <T>Imprimir QR</T>
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default PaymentQRGenerator;